import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme configuration with proper contrast ratios
const themes = {
  dark: {
    // Backgrounds
    primary: 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900',
    secondary: 'bg-black/20 backdrop-blur-sm',
    card: 'bg-black/30',
    input: 'bg-black/30',
    sidebar: 'bg-black/30 backdrop-blur-md',
    
    // Borders
    border: 'border-military-700/30',
    borderInput: 'border-military-700/50',
    borderFocus: 'focus:border-military-500',
    
    // Text colors (WCAG AA compliant)
    textPrimary: 'text-white',
    textSecondary: 'text-gray-300',
    textMuted: 'text-gray-400',
    textAccent: 'text-military-400',
    
    // Button colors
    buttonPrimary: 'bg-military-600 hover:bg-military-700 text-white',
    buttonSecondary: 'bg-gray-600 hover:bg-gray-700 text-white',
    buttonDanger: 'bg-red-600 hover:bg-red-700 text-white',
    
    // Toggle colors
    toggleBg: 'bg-gray-600',
    toggleActive: 'peer-checked:bg-military-600',
    toggleThumb: 'after:bg-white',
    
    // Slider colors
    sliderBg: 'bg-gray-600',
    
    // Warning colors
    warningBg: 'bg-yellow-900/20',
    warningBorder: 'border-yellow-600/30',
    warningText: 'text-yellow-400',
    
    // Dashboard specific
    dashboardCard: 'bg-black/30 backdrop-blur-md border-military-700/30',
    dashboardCardHover: 'hover:bg-black/40 hover:border-blue-500/50 hover:scale-105 hover:shadow-lg',
    
    // Chart colors
    chartBackground: 'bg-black/20',
    chartText: 'text-white',
    
    // Map colors
    mapContainer: 'bg-black/20',
    
    // Body background
    bodyBg: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)'
  },
  light: {
    // Backgrounds - Enhanced contrast
    primary: 'bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100',
    secondary: 'bg-white/95 backdrop-blur-sm shadow-sm',
    card: 'bg-white shadow-sm',
    input: 'bg-white',
    sidebar: 'bg-white shadow-lg border-r',
    
    // Borders - Stronger definition
    border: 'border-gray-300',
    borderInput: 'border-gray-400',
    borderFocus: 'focus:border-military-600 focus:ring-2 focus:ring-military-200',
    
    // Text colors (Enhanced WCAG AAA compliant)
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-800',
    textMuted: 'text-gray-600',
    textAccent: 'text-military-700',
    
    // Button colors - Better contrast
    buttonPrimary: 'bg-military-700 hover:bg-military-800 text-white shadow-md',
    buttonSecondary: 'bg-gray-600 hover:bg-gray-700 text-white shadow-md',
    buttonDanger: 'bg-red-600 hover:bg-red-700 text-white shadow-md',
    
    // Toggle colors - Enhanced visibility
    toggleBg: 'bg-gray-400',
    toggleActive: 'peer-checked:bg-military-600',
    toggleThumb: 'after:bg-white after:shadow-sm',
    
    // Slider colors - Better visibility
    sliderBg: 'bg-gray-400',
    
    // Warning colors - Enhanced contrast
    warningBg: 'bg-amber-50 border',
    warningBorder: 'border-amber-400',
    warningText: 'text-amber-800',
    
    // Dashboard specific - High contrast
    dashboardCard: 'bg-white shadow-md border border-gray-200',
    dashboardCardHover: 'hover:bg-gray-50 hover:border-military-400 hover:scale-105 hover:shadow-lg',
    
    // Chart colors - Optimized for light backgrounds
    chartBackground: 'bg-white shadow-sm border border-gray-200',
    chartText: 'text-gray-900',
    
    // Map colors - Light theme optimized
    mapContainer: 'bg-white shadow-sm border border-gray-200',
    
    // Body background - Clean light gradient
    bodyBg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
    
    // Dashboard specific
    dashboardCard: 'bg-white/90 backdrop-blur-md border-gray-200',
    dashboardCardHover: 'hover:bg-white hover:border-military-300 hover:scale-105 hover:shadow-lg',
    
    // Chart colors
    chartBackground: 'bg-white/80',
    chartText: 'text-gray-900',
    
    // Map colors
    mapContainer: 'bg-white/80',
    
    // Body background
    bodyBg: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)'
  }
};

// Create theme context
const ThemeContext = createContext();

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('fracadapt-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        if (parsed.display && parsed.display.theme) {
          setTheme(parsed.display.theme);
        }
      } catch (error) {
        console.error('Failed to load theme from settings:', error);
      }
    }
  }, []);

  // Update body background when theme changes
  useEffect(() => {
    const body = document.body;
    const themeConfig = themes[theme] || themes.dark;
    
    // Remove existing theme classes
    body.className = body.className.replace(/theme-\w+/g, '');
    
    // Add new theme class
    body.classList.add(`theme-${theme}`);
    
    // Update body background
    body.style.background = themeConfig.bodyBg;
  }, [theme]);

  // Get current theme classes
  const getThemeClasses = () => themes[theme] || themes.dark;

  // Change theme and save to localStorage
  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    
    // Update localStorage
    const savedSettings = localStorage.getItem('fracadapt-settings');
    let settings = {};
    
    if (savedSettings) {
      try {
        settings = JSON.parse(savedSettings);
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
    
    // Update theme in settings
    settings.display = { ...settings.display, theme: newTheme };
    
    try {
      localStorage.setItem('fracadapt-settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save theme to localStorage:', error);
    }
  };

  const value = {
    theme,
    setTheme: changeTheme,
    getThemeClasses,
    themes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
